import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  AuthService,
  BaseSiteService,
  ConfigInitializerService,
  getContextParameterDefault,
  SiteContextConfig,
  User
} from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { Observable, switchMap } from 'rxjs';
import { distinctUntilChanged, filter, first, map, take, tap } from 'rxjs/operators';

import { DpMeasurementSystemStatePersistenceService } from './measurement-system-state-persistence.service';

import { MEASUREMENT_SYSTEM_CONTEXT_ID } from '../constants/measurement-system-context-id.constant';
import { DpMeasurementSystemService } from '../facade/measurement-system.service';

@Injectable({ providedIn: 'root' })
export class DpMeasurementSystemInitializer {
  readonly #destroyRef = inject(DestroyRef);
  readonly #measurementSystemService = inject(DpMeasurementSystemService);
  readonly #statePersistenceService = inject(DpMeasurementSystemStatePersistenceService);
  readonly #configInit = inject(ConfigInitializerService);
  readonly #baseSiteService = inject(BaseSiteService);
  readonly #authService = inject(AuthService);
  readonly #userService = inject(UserAccountFacade);

  initialize(): void {
    this.#staticInitialize();
    this.#multiSiteInitialize();
  }

  #staticInitialize(): void {
    this.#configInit
      .getStable('context')
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        switchMap(() => this.#statePersistenceService.initSync()),
        switchMap(() => this.#setFallbackValue())
      )
      .subscribe();
  }

  #multiSiteInitialize(): void {
    this.#authService
      .isUserLoggedIn()
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        distinctUntilChanged(),
        switchMap((isLoggedIn) =>
          isLoggedIn ? this.#handleMeasurementSystemForLoggedInUser() : this.#handleMeasurementSystemForAnonymous()
        )
      )
      .subscribe((code) => {
        if (code.length) {
          this.#measurementSystemService.setActive(code);
        }
      });
  }

  #handleMeasurementSystemForAnonymous(): Observable<string> {
    return this.#baseSiteService.getActive().pipe(
      takeUntilDestroyed(this.#destroyRef),
      distinctUntilChanged(),
      filter(Boolean),
      switchMap((baseSite) => this.#baseSiteService.get(baseSite).pipe(take(1))),
      map((baseSite) => baseSite?.defaultMeasurementSystem?.code ?? '')
    );
  }

  #handleMeasurementSystemForLoggedInUser(): Observable<string> {
    return (this.#userService.get() as Observable<User>).pipe(
      first((user) => !!user),
      map((user: User) => user?.measurementSystem?.code ?? '')
    );
  }

  #setFallbackValue(): Observable<unknown> {
    return this.#configInit
      .getStable('context')
      .pipe(tap((config: SiteContextConfig) => this.#setDefaultFromConfig(config)));
  }

  #setDefaultFromConfig(config: SiteContextConfig): void {
    const contextParam = getContextParameterDefault(config, MEASUREMENT_SYSTEM_CONTEXT_ID);
    if (!this.#measurementSystemService.isInitialized() && contextParam) {
      this.#measurementSystemService.setActive(contextParam);
    }
  }
}
